import {
  AlvysConnection,
  AlvysConnectionUpdate,
  AlvysKeyStatus,
  AlvysSecret
} from "../../../../../../api/factoring/alvys";
import colors from "../../../../../../theme/colors";

export interface AlvysFormValues {
  clientId: string;
  clientSecret: string;
  webhookSecret: string;
  keyExpiresOn: string;
  newKeyExpiresOn: string;
}

export const KEY_STATUS: Record<
  AlvysKeyStatus,
  { label: string; color: string }
> = {
  not_configured: { label: "NO KEY", color: colors.grayText },
  ok: { label: "KEY ACTIVE", color: colors.green },
  expiring_soon: { label: "KEY EXPIRES SOON", color: colors.orange },
  expired: { label: "KEY EXPIRED", color: colors.danger },
  rejected: { label: "KEY REJECTED", color: colors.danger }
};

export const maskedSecret = (secret: AlvysSecret): string =>
  `••••${secret.last4}`;

export const expiryNote = (
  status: AlvysKeyStatus
): { text: string; color?: string } => {
  if (status === "expired") {
    return {
      text: "Expired. Enter a new key from Alvys.",
      color: colors.danger
    };
  }
  if (status === "expiring_soon") {
    return { text: "Expires within 30 days.", color: colors.orange };
  }
  return { text: "The broker is reminded 30 and 7 days before." };
};

export const NEW_KEY_EXPIRY_NOTE = {
  text: "Enter the date this new key expires in Alvys.",
  color: colors.orange
};

export const formValuesFrom = (
  connection: AlvysConnection
): AlvysFormValues => ({
  clientId: connection.client_id,
  clientSecret: "",
  webhookSecret: "",
  keyExpiresOn: connection.key_expires_on || "",
  newKeyExpiresOn: ""
});

export const isNewKey = (
  connection: AlvysConnection,
  values: AlvysFormValues
): boolean =>
  Boolean(values.clientSecret) ||
  values.clientId.trim() !== connection.client_id;

export const changedValues = (
  connection: AlvysConnection,
  values: AlvysFormValues
): AlvysConnectionUpdate => {
  const update: AlvysConnectionUpdate = {};
  const clientId = values.clientId.trim();
  if (clientId !== connection.client_id) update.client_id = clientId;
  if (values.clientSecret) update.client_secret = values.clientSecret;
  if (values.webhookSecret) update.webhook_secret = values.webhookSecret;
  if (isNewKey(connection, values)) {
    update.key_expires_on = values.newKeyExpiresOn || null;
    return update;
  }
  const keyExpiresOn = values.keyExpiresOn || null;
  if (keyExpiresOn !== connection.key_expires_on) {
    update.key_expires_on = keyExpiresOn;
  }
  return update;
};

export const clientIdMissing = (
  connection: AlvysConnection,
  values: AlvysFormValues
): boolean => isNewKey(connection, values) && !values.clientId.trim();

export const clientSecretMissing = (
  connection: AlvysConnection,
  values: AlvysFormValues
): boolean => {
  const clientId = values.clientId.trim();
  return (
    Boolean(clientId) &&
    clientId !== connection.client_id &&
    !values.clientSecret
  );
};

export const hasCredentials = (connection: AlvysConnection): boolean =>
  Boolean(
    connection.client_id ||
      connection.client_secret.configured ||
      connection.webhook_secret.configured
  );
